import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, apiErr, fmtMoney, fmtDate, API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  healthy: "bg-emerald-100 text-emerald-700", sick: "bg-red-100 text-red-700",
  pregnant: "bg-amber-100 text-amber-700", sold: "bg-slate-100 text-slate-600",
  deceased: "bg-slate-200 text-slate-500",
  planned: "bg-slate-100 text-slate-600", planted: "bg-sky-100 text-sky-700",
  growing: "bg-emerald-100 text-emerald-700", harvested: "bg-amber-100 text-amber-700",
  draft: "bg-slate-100 text-slate-600", sent: "bg-sky-100 text-sky-700",
  paid: "bg-emerald-100 text-emerald-700", overdue: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-700", income: "bg-emerald-100 text-emerald-700",
  expense: "bg-red-100 text-red-700",
};

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatusBadge({ value }) {
  if (!value) return <span>—</span>;
  return <Badge className={`${STATUS_COLORS[value] || "bg-slate-100 text-slate-600"} border-0 capitalize`}>{value}</Badge>;
}

function renderCell(col, row) {
  const v = row[col.key];
  if (col.type === "money") return <span className="num">{fmtMoney(v)}</span>;
  if (col.type === "date") return fmtDate(v);
  if (col.type === "badge") return <StatusBadge value={v} />;
  if (col.type === "number") return <span className="num">{v ?? 0}</span>;
  if (col.type === "image") return v
    ? <img src={`${API}/files/${v}`} alt="" className="h-10 w-10 rounded-md border border-border object-cover" />
    : <span className="text-muted-foreground">—</span>;
  if (col.type === "bool") return v
    ? <Badge className="border-0 bg-emerald-100 text-emerald-700">Yes</Badge>
    : <Badge className="border-0 bg-slate-100 text-slate-500">No</Badge>;
  if (col.type === "file") return v
    ? <a href={`${API}/files/${v}`} target="_blank" rel="noreferrer" className="font-medium text-primary underline">View</a>
    : <span className="text-muted-foreground">—</span>;
  return v || "—";
}

export default function ResourcePage({ title, subtitle, endpoint, columns, fields, testid, emptyHint, embedded }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [delId, setDelId] = useState(null);
  const [relOpts, setRelOpts] = useState({});
  const [manual, setManual] = useState({}); // { [fieldKey]: true }

  const relationEndpoints = useMemo(() => {
    const set = new Set();
    fields.forEach((f) => {
      if (f.type === "relation") {
        if (f.endpoint) set.add(f.endpoint);
        if (f.endpointByField) Object.values(f.endpointByField.map).forEach((e) => set.add(e));
      }
    });
    return [...set];
  }, [fields]);

  const loadRelations = useCallback(async () => {
    if (!relationEndpoints.length) return;
    const entries = await Promise.all(relationEndpoints.map(async (ep) => {
      try { const { data } = await api.get(`/${ep}`); return [ep, data]; }
      catch { return [ep, []]; }
    }));
    setRelOpts(Object.fromEntries(entries));
  }, [relationEndpoints]);
  useEffect(() => { loadRelations(); }, [loadRelations]);

  const relEndpointFor = (f) =>
    f.endpointByField ? f.endpointByField.map[form[f.endpointByField.field]] : f.endpoint;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => {
    const init = {};
    fields.forEach((f) => { init[f.key] = f.default ?? (f.type === "number" ? 0 : f.type === "checkbox" ? false : f.type === "select" ? f.options[0].value : ""); });
    setForm(init); setEditing(null); setManual({}); loadRelations(); setOpen(true);
  };
  const openEdit = (row) => {
    const init = {};
    fields.forEach((f) => { init[f.key] = row[f.key] ?? (f.type === "checkbox" ? false : ""); });
    // preserve relation id fields + start manual mode for legacy free-text values
    const man = {};
    fields.forEach((f) => {
      if (f.type === "relation") {
        if (f.idField) init[f.idField] = row[f.idField] ?? "";
        if (row[f.key] && !row[f.idField]) man[f.key] = true;
      }
    });
    setForm(init); setEditing(row.id); setManual(man); loadRelations(); setOpen(true);
  };

  const uploadField = async (key, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: data.file_id }));
      toast.success("File uploaded");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key] || 0); });
    try {
      if (editing) await api.put(`/${endpoint}/${editing}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${delId}`);
      toast.success("Deleted"); setDelId(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const RelationField = ({ f }) => {
    const ep = relEndpointFor(f);
    const options = relOpts[ep] || [];
    const label = f.optionLabel || "name";
    const isManual = !!manual[f.key] || !ep;
    const selectVal = form[f.idField] && options.some((o) => o.id === form[f.idField]) ? form[f.idField] : "";
    const choose = (v) => {
      if (v === "__manual__") { setManual((m) => ({ ...m, [f.key]: true })); setForm((s) => ({ ...s, [f.idField]: "" })); return; }
      const opt = options.find((o) => o.id === v);
      if (!opt) return;
      setForm((s) => {
        const next = { ...s, [f.key]: opt[label], [f.idField]: opt.id };
        if (f.fill) Object.entries(f.fill).forEach(([t, src]) => { if (opt[src] != null && opt[src] !== "") next[t] = opt[src]; });
        return next;
      });
    };
    if (isManual) {
      return (
        <div className="mt-1.5">
          <Input value={form[f.key] ?? ""} placeholder={f.placeholder || "Type a name"}
            onChange={(e) => setForm({ ...form, [f.key]: e.target.value, [f.idField]: "" })}
            data-testid={`${testid}-field-${f.key}`} />
          {ep && options.length > 0 && (
            <button type="button" className="mt-1 text-xs text-primary hover:underline"
              onClick={() => { setManual((m) => { const n = { ...m }; delete n[f.key]; return n; }); setForm((s) => ({ ...s, [f.key]: "", [f.idField]: "" })); }}
              data-testid={`${testid}-field-${f.key}-fromlist`}>
              Choose from list
            </button>
          )}
        </div>
      );
    }
    return (
      <Select value={selectVal} onValueChange={choose}>
        <SelectTrigger className="mt-1.5" data-testid={`${testid}-field-${f.key}`}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o[label]}{o.category ? ` · ${o.category}` : ""}</SelectItem>)}
          {f.allowCustom && <SelectItem value="__manual__">✏️ Enter manually…</SelectItem>}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="fade-up">
      {embedded ? (
        <div className="mb-4 flex items-center justify-between">
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          <Button onClick={openNew} data-testid={`${testid}-add`}><Plus className="mr-1.5 h-4 w-4" />Add</Button>
        </div>
      ) : (
        <PageHeader title={title} subtitle={subtitle}
          action={<Button onClick={openNew} data-testid={`${testid}-add`}><Plus className="mr-1.5 h-4 w-4" />Add</Button>} />
      )}

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={c.type === "money" || c.type === "number" ? "text-right" : ""}>{c.label}</TableHead>
              ))}
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground">{emptyHint || "Nothing here yet. Click Add to get started."}</TableCell></TableRow>
            ) : items.map((row) => (
              <TableRow key={row.id} data-testid={`${testid}-row`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={`text-sm ${c.type === "money" || c.type === "number" ? "text-right" : ""} ${c.bold ? "font-semibold" : ""}`}>
                    {renderCell(c, row)}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`${testid}-edit`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(row.id)} data-testid={`${testid}-delete`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</DialogTitle>
            <DialogDescription>Fill in the details below and save.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{f.label}</Label>
                {f.type === "relation" ? (
                  <RelationField f={f} />
                ) : f.type === "select" ? (
                  <Select value={String(form[f.key] ?? "")} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="mt-1.5" data-testid={`${testid}-field-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea className="mt-1.5" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-field-${f.key}`} />
                ) : f.type === "checkbox" ? (
                  <div className="mt-2"><Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} data-testid={`${testid}-field-${f.key}`} /></div>
                ) : f.type === "image" || f.type === "file" ? (
                  <div className="mt-1.5 flex items-center gap-3">
                    {form[f.key] ? (
                      <img src={`${API}/files/${form[f.key]}`} alt="" className="h-14 w-14 rounded-md border border-border object-cover" />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-border text-[10px] text-muted-foreground">None</div>
                    )}
                    <Input type="file" accept={f.type === "image" ? "image/*" : "image/*,application/pdf"} className="text-xs" onChange={(e) => uploadField(f.key, e.target.files[0])} data-testid={`${testid}-field-${f.key}`} />
                  </div>
                ) : (
                  <Input className="mt-1.5" type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid={`${testid}-save`}>{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid={`${testid}-confirm-delete`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
